import {inject, Injectable, InjectionToken} from '@angular/core';
import {Location} from '@angular/common';
import {
    Event, Navigation, NavigationCancel, NavigationCancellationCode, NavigationEnd, NavigationError, NavigationStart, Router,
    UrlTree
} from '@angular/router';

// Kept in each history entry's state. The router's navigationId cannot answer the question: it counts
// navigations, and a browser back or forward rewrites the entry with a fresh, higher one. Other keys of the
// state survive that rewrite, so the entry carries its own answer back.
const AFTER_APP_PAGE = 'idcarAfterAppPage';
// The router's own key for an entry's place in the session history, read by canceledNavigationResolution
// 'computed'. Internal to Angular (15.2.10), as is the router's currentPageId: check restore(), adoptEntry(),
// placeEntry() and write() when upgrading.
const ROUTER_PAGE_ID = 'ɵrouterPageId';

// The browser's own index of its current history entry (the Navigation API's navigation.currentEntry.index), or
// undefined where the browser has no Navigation API (Chrome and Edge before 102, Firefox before 147, Safari before
// 26.2).
export const HISTORY_INDEX = new InjectionToken<() => number | undefined>('HISTORY_INDEX', {
    providedIn: 'root',
    factory: () => () => {
        const index = (window as unknown as {navigation?: {currentEntry?: {index: number} | null}}).navigation
            ?.currentEntry?.index;

        return index !== undefined && index >= 0 ? index : undefined;
    }
});

interface Arrival {
    url: string;
    state: Record<string, unknown> | null;
}

// Whether the browser's previous history entry is a page of this app, so a back arrow can go back to it
// rather than out of the app. Started once by the root component, before the first navigation starts: it acts on
// that navigation's NavigationStart (placeEntry, adoptEntry) as well as on its end.
@Injectable({providedIn: 'root'})
export class AppHistoryService {
    private router = inject(Router);
    private location = inject(Location);
    private historyIndex = inject(HISTORY_INDEX);
    private current = false;
    private started = false;
    private shown = false;
    // The page id of the page's own entry, read when a navigation ends: its index in the history, or, without the
    // Navigation API, the router's count from the entry the app was opened on. Undefined for an entry whose place
    // cannot be told (see placed()).
    private pageId: unknown;
    // The router's private page id of its current page, which its own restoreHistory compares with the entry's.
    private routerPage = this.router as unknown as {currentPageId: unknown};
    // Whether the browser was on an entry with a page id when the navigation in progress started.
    private startedPlaced = false;
    // A browser back or forward in progress between entries whose distance cannot be told, and the entry it went
    // to, as it was when the browser got there.
    private unplacedArrival: Arrival | null = null;
    // The navigation completeTraversal() starts: about to start, then its id. A link or redirect may supersede it.
    private completionStarting = false;
    private completionId: number | null = null;

    start(): void {
        if (this.started) {
            return;
        }

        this.started = true;
        this.current = this.entryAnswer();
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                if (this.completionStarting) {
                    this.completionStarting = false;
                    this.completionId = event.id;
                }

                this.placeEntry();
                this.startedPlaced = this.placed(this.state());

                if (!this.shown) {
                    this.adoptEntry();
                } else if (event.navigationTrigger !== 'imperative') {
                    this.arrive();
                }
            } else if (event instanceof NavigationEnd) {
                this.record();
            } else if (this.restoresHistory(event)) {
                this.restore(event);
            }
        });
    }

    hasAppPageBefore(): boolean {
        return this.entryAnswer();
    }

    // Whether the navigation in progress can be undone if a guard rejects it. It cannot be for a browser back or
    // forward onto or from an entry whose place in the history is unknown (see arrive()): a guard that would ask
    // before leaving lets such a navigation through without asking, rather than offer «Нет» and not keep to it.
    canUndoNavigation(): boolean {
        return this.unplacedArrival === null;
    }

    private entryAnswer(): boolean {
        return this.state()?.[AFTER_APP_PAGE] === true;
    }

    private state(): Record<string, unknown> | null {
        return this.location.getState() as Record<string, unknown> | null;
    }

    private placed(state: Record<string, unknown> | null): boolean {
        return state?.[ROUTER_PAGE_ID] !== undefined;
    }

    // Only a rejected guard, a resolver with no data and an error undo a navigation. A navigation superseded by a
    // newer one, e.g. a second browser back, leaves the browser on another entry, whose answer is its own; a
    // redirect is followed by the navigation it redirects to.
    private restoresHistory(event: Event): event is NavigationCancel | NavigationError {
        return event instanceof NavigationError
            || (event instanceof NavigationCancel
                && (event.code === NavigationCancellationCode.GuardRejected
                    || event.code === NavigationCancellationCode.NoDataFromResolver));
    }

    // With canceledNavigationResolution 'computed' (app-routing.module.ts) the router undoes a navigation by where
    // the browser is, not by how the navigation started: a guard's redirect is imperative, yet may start on an
    // entry a browser back went to.
    private restore(event: NavigationCancel | NavigationError): void {
        const state = this.state();
        const entryPageId = state?.[ROUTER_PAGE_ID];
        const arrival = this.unplacedArrival;

        if (arrival && this.shown) {
            this.completeTraversal(event, arrival);
            return;
        }

        if (entryPageId !== undefined && entryPageId !== this.routerPage.currentPageId) {
            // The browser is on another entry, and the router sends it back to the page's with history.go(), which
            // moves only after this event. That entry keeps its own answer, and the page's is read back by record()
            // when the router navigates to it again. An entry this navigation pushed before it failed is still one
            // an app page led to; one it replaced (a browser back or forward, or a redirect from one) is not. A
            // browser back or forward keeps the entry's answer in its state; a redirect a guard makes by returning a
            // UrlTree does not (Angular 15.2.10 starts it with no state), so an entry it replaced is left with the
            // target's URL and no answer if the target then fails. No guard in the app returns a UrlTree. The failed
            // navigation is still the router's current one here.
            if (state?.['navigationId'] === event.id && this.pushed(this.router.getCurrentNavigation())) {
                // As in recordedPageId(): pushed from an entry with no id, the router's id for it is wrong.
                const pushedPageId = this.historyIndex() !== undefined || this.startedPlaced ? entryPageId : undefined;

                this.location.replaceState(this.location.path(true), '', this.pageState(state, true, pushedPageId));
            }

            return;
        }

        if (!this.shown) {
            // The navigation the app was opened with: there is no page to put back, only this entry's answer.
            this.write(this.current, this.historyIndex() ?? entryPageId ?? 0);
            return;
        }

        // The browser stays on the page's own entry, which the router may have rewritten with a state of its own,
        // dropping the answer.
        this.write(this.current, this.pageId, this.router.url);
    }

    // Angular 15.2.10 starts the router's own page id (private currentPageId) at 0 and corrects it only when a
    // navigation succeeds. On an entry that already has a page id N (a reload, or a back from another site) a first
    // navigation that fails would send the browser N entries back. Until a page has been shown, the router's page
    // is the entry's own, so a failure leaves the browser where it is; and the answer to put back is the entry's,
    // read before the router can rewrite its state.
    private adoptEntry(): void {
        this.routerPage.currentPageId = this.state()?.[ROUTER_PAGE_ID] ?? 0;
        this.current = this.entryAnswer();
    }

    // The router reads an entry's page id to tell how far to move the browser back when it undoes a navigation; it
    // only works if every entry's id is its place in the history. Entries from before the upgrade have none, and the
    // router's own ids count from the entry the app was first opened on, as if it were the first. Where the browser
    // knows the entry's index, that is the id, given before the router reads it.
    private placeEntry(): void {
        const index = this.historyIndex();
        const state = this.state();

        if (index !== undefined && state?.[ROUTER_PAGE_ID] !== index) {
            this.location.replaceState(this.location.path(true), '', {...state, [ROUTER_PAGE_ID]: index});
        }
    }

    // Without the Navigation API, a browser back or forward onto an entry with no page id, or from a page whose
    // entry has none, goes a distance nobody can tell, so it cannot be undone without losing entries: the router
    // must not move the browser back (it would move it by a wrong count), and pushing or rewriting an entry would
    // drop or overwrite one. Its page id is made the entry's, so the router moves it by none, and the entry is kept
    // as it is now for completeTraversal().
    private arrive(): void {
        const state = this.state();

        this.unplacedArrival = null;

        if (this.historyIndex() !== undefined || (this.placed(state) && this.pageId !== undefined)) {
            return;
        }

        this.unplacedArrival = {url: this.location.path(true), state};

        if (this.placed(state)) {
            this.routerPage.currentPageId = state?.[ROUTER_PAGE_ID];
        }
    }

    // A browser back or forward that cannot be undone, yet was rejected or failed: the browser stays where it went,
    // and no entry is added or dropped. The entry is put back as it was when the browser got there (after a failed
    // render the router has rewritten it with the page's URL). After a rejection the router is sent to it, with its
    // own state and answer, replacing nothing but that entry. A failure is not retried, nor a rejection of that
    // navigation too: the browser then shows the entry's URL with the page left behind, its place unknown.
    private completeTraversal(event: NavigationCancel | NavigationError, arrival: Arrival): void {
        this.location.replaceState(arrival.url, '', arrival.state);

        if (event instanceof NavigationError || event.id === this.completionId) {
            this.unplacedArrival = null;
            this.completionId = null;
            this.pageId = undefined;
            return;
        }

        const state: Record<string, unknown> = {...arrival.state};

        delete state['navigationId'];
        delete state[ROUTER_PAGE_ID];
        this.completionStarting = true;
        this.router.navigateByUrl(arrival.url, {replaceUrl: true, state});
    }

    private pushed(navigation: Navigation | null): boolean {
        return navigation?.trigger === 'imperative' && !navigation.extras.replaceUrl && !navigation.extras.skipLocationChange;
    }

    // The router navigating to the URL it is on replaces the entry instead of adding one.
    private sameUrl(a: UrlTree | undefined, b: UrlTree | undefined): boolean {
        return !!a && !!b && this.router.serializeUrl(a) === this.router.serializeUrl(b);
    }

    private record(): void {
        const navigation = this.router.getCurrentNavigation();

        const completing = navigation?.id === this.completionId;

        this.unplacedArrival = null;
        this.completionId = null;

        if (!navigation || navigation.extras.skipLocationChange) {
            return;
        }

        const previous = navigation.previousNavigation;
        let after: boolean;

        if (navigation.trigger !== 'imperative' || !previous || completing) {
            // A browser back or forward (completeTraversal() finishing one among them), or the page the app was
            // opened or reloaded on: the entry's own answer.
            after = this.entryAnswer();
        } else if (navigation.extras.replaceUrl || this.sameUrl(previous.finalUrl, navigation.finalUrl)) {
            // The router replaced the entry rather than adding one, and dropped the answer with its state.
            after = this.current;
        } else {
            after = true;
        }

        this.write(after, this.recordedPageId(navigation));
        this.shown = true;
    }

    // Without the Navigation API: the entry the app was opened on is counted from (the router assumes 0 for it,
    // leaving it without one); an entry the browser went back or forward to keeps the id it has, or its lack of one;
    // an entry a link led to from an entry without an id gets none either, the router's id for it (counted as if
    // from 0) being wrong.
    private recordedPageId(navigation: Navigation): unknown {
        const pageId = this.state()?.[ROUTER_PAGE_ID];

        if (this.historyIndex() !== undefined) {
            return this.historyIndex();
        } else if (!this.shown) {
            return pageId ?? 0;
        } else if (navigation.trigger === 'imperative' && !this.startedPlaced) {
            return undefined;
        }

        return pageId;
    }

    private write(after: boolean, pageId: unknown, url = this.location.path(true)): void {
        this.current = after;
        this.pageId = pageId;
        this.location.replaceState(url, '', this.pageState(this.state(), after, pageId));
    }

    private pageState(state: Record<string, unknown> | null, after: boolean, pageId: unknown): Record<string, unknown> {
        const pageState: Record<string, unknown> = {...state, [AFTER_APP_PAGE]: after};

        if (pageId === undefined) {
            delete pageState[ROUTER_PAGE_ID];
        } else {
            pageState[ROUTER_PAGE_ID] = pageId;
        }

        return pageState;
    }
}
